/**
 * Login page component.
 */
import React, { useState, FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { initiateOAuth2Login } from '../api/oauth2';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for OAuth2 callback messages
  React.useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    
    // Only set error if we have a non-empty, non-null value
    if (errorParam && errorParam !== 'null' && errorParam.trim().length > 0) {
      setError(errorParam);
    } else if (messageParam && messageParam !== 'null' && messageParam.trim().length > 0) {
      setError(messageParam);
    }
  }, [searchParams]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/projects', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // Only navigate on success - clear any previous errors
      setError('');
      navigate('/projects');
    } catch (err: any) {
      // Extract error message from various possible error formats
      let errorMessage = 'Login failed. Please check your credentials.';
      
      // Handle different error response formats
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        // FastAPI validation errors have detail as array or string
        if (Array.isArray(errorData.detail)) {
          // Validation errors - combine all messages
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      // Always set error message and stop loading
      setError(errorMessage);
      setIsLoading(false);
      // Keep error visible - don't clear it automatically
    } finally {
      // Ensure loading state is always reset
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>{settings?.app_name || 'dasher'}</h1>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" role="form">
          {error && error.length > 0 && (
            <div id="error-message" className="error-message" role="alert" aria-live="polite">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear error when user starts typing
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Enter your email"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'error-message' : undefined}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Clear error when user starts typing
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Enter your password"
              minLength={8}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'error-message' : undefined}
            />
          </div>

          <button type="submit" disabled={isLoading} className="login-button">
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {settings?.signup_enabled && (
          <div className="signup-link">
            <p>
              Don't have an account? <Link to="/signup">Sign up</Link>
            </p>
          </div>
        )}

        {settings?.oauth2_provider && settings?.oauth2_provider !== 'disabled' && (
          <div className="oauth-section">
            <div className="oauth-divider">
              <span>or</span>
            </div>
            <button
              type="button"
              onClick={initiateOAuth2Login}
              disabled={isLoading}
              className="oauth-button"
            >
              Sign in with {settings.oauth2_provider.charAt(0).toUpperCase() + settings.oauth2_provider.slice(1)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

